// sdks/web/src/offline-queue.ts
import type { QueuedEvent } from "./queue.js";

export interface OfflineQueue {
  save(events: QueuedEvent[]): Promise<void>;
  drain(): Promise<QueuedEvent[]>;
  size(): Promise<number>;
  close(): void;
  /** Number of events dropped because they exceeded `maxBytes` since this queue was created. */
  readonly overflowDrops?: number;
}

export interface OfflineQueueOptions {
  dbName?: string;
  storeName?: string;
  maxEvents?: number;
  maxBytes?: number;
  /**
   * If true (default), oversized events are dropped with a counter bump +
   * console warning. If false, `save()` throws `QueueOverflowError` instead.
   */
  dropOversized?: boolean;
  /**
   * Optional sink for overflow signals. Invoked once per oversized event.
   * The SDK wires this to the EventBus so listeners can observe
   * `sdk:queue-overflow` events.
   */
  onOverflow?: (event: QueuedEvent, size: number, maxBytes: number) => void;
}

const DEFAULTS = {
  dbName: "ets_offline",
  storeName: "pending_events",
  maxEvents: 1000,
  maxBytes: 1_000_000,
  dropOversized: true,
};

/**
 * Error thrown by `OfflineQueue.save` when `dropOversized` is `false` and
 * at least one event exceeds the configured `maxBytes` budget. Exposes the
 * offending size and the configured limit.
 */
export class QueueOverflowError extends Error {
  readonly size: number;
  readonly maxBytes: number;
  constructor(size: number, maxBytes: number) {
    super(
      `ETS OfflineQueue: event size ${size} exceeds maxBytes ${maxBytes}`,
    );
    this.name = "QueueOverflowError";
    this.size = size;
    this.maxBytes = maxBytes;
  }
}

function makeNoop(): OfflineQueue {
  return {
    save: async () => undefined,
    drain: async () => [],
    size: async () => 0,
    close: () => undefined,
    overflowDrops: 0,
  };
}

export function createOfflineQueue(opts: OfflineQueueOptions = {}): OfflineQueue {
  if (typeof indexedDB === "undefined") return makeNoop();

  const dbName = opts.dbName ?? DEFAULTS.dbName;
  const storeName = opts.storeName ?? DEFAULTS.storeName;
  const maxEvents = opts.maxEvents ?? DEFAULTS.maxEvents;
  const maxBytes = opts.maxBytes ?? DEFAULTS.maxBytes;
  const dropOversized = opts.dropOversized ?? DEFAULTS.dropOversized;
  const onOverflow = opts.onOverflow;

  let dbPromise: Promise<IDBDatabase> | null = null;
  let overflowDrops = 0;

  function openDb(): Promise<IDBDatabase> {
    if (dbPromise) return dbPromise;
    dbPromise = new Promise((resolve, reject) => {
      const req = indexedDB.open(dbName, 1);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains(storeName)) {
          db.createObjectStore(storeName, { keyPath: "id", autoIncrement: true });
        }
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
    return dbPromise;
  }

  async function tx(mode: IDBTransactionMode): Promise<IDBObjectStore> {
    const db = await openDb();
    return db.transaction(storeName, mode).objectStore(storeName);
  }

  async function allKeys(store: IDBObjectStore): Promise<IDBValidKey[]> {
    return await new Promise((resolve, reject) => {
      const req = store.getAllKeys();
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
  }

  const queue: OfflineQueue = {
    async save(events) {
      if (events.length === 0) return;
      const store = await tx("readwrite");
      for (const e of events) {
        const size = JSON.stringify(e).length;
        if (size > maxBytes) {
          // Never drop silently — signal via counter + optional sink.
          overflowDrops++;
          onOverflow?.(e, size, maxBytes);
          if (!dropOversized) {
            throw new QueueOverflowError(size, maxBytes);
          }
          // biome-ignore lint/suspicious/noConsole: observability for data-loss event
          console.warn(
            `ETS OfflineQueue: dropping oversized event (${size} bytes > ${maxBytes})`,
          );
          continue;
        }
        store.add({ event: e, savedAt: Date.now() });
      }
      const keys = await allKeys(store);
      const excess = keys.length - maxEvents;
      if (excess > 0) {
        for (let i = 0; i < excess; i++) {
          const k = keys[i];
          if (k !== undefined) store.delete(k);
        }
      }
      await new Promise<void>((resolve, reject) => {
        store.transaction.oncomplete = () => resolve();
        store.transaction.onerror = () => reject(store.transaction.error);
      });
    },

    async drain() {
      const store = await tx("readwrite");
      const rows: Array<{ id: number; event: QueuedEvent }> = await new Promise(
        (resolve, reject) => {
          const req = store.getAll();
          req.onsuccess = () => resolve(req.result as typeof rows);
          req.onerror = () => reject(req.error);
        },
      );
      rows.sort((a, b) => a.id - b.id);
      await new Promise<void>((resolve, reject) => {
        const clearReq = store.clear();
        clearReq.onsuccess = () => resolve();
        clearReq.onerror = () => reject(clearReq.error);
      });
      return rows.map((r) => r.event);
    },

    async size() {
      const store = await tx("readonly");
      return await new Promise((resolve, reject) => {
        const req = store.count();
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
      });
    },

    close() {
      dbPromise?.then((db) => db.close()).catch(() => undefined);
      dbPromise = null;
    },

    get overflowDrops() {
      return overflowDrops;
    },
  };

  return queue;
}
